import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Star, MessageSquare, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/format';
import { isSubHidden } from '@/lib/auth';

function renderAnswer(field, value) {
    if (value === undefined || value === null || value === '') return <span className="text-muted-foreground">—</span>;
    if (field?.type === 'rating') {
        return (
            <span className="inline-flex items-center gap-0.5 text-amber-500">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= Number(value) ? 'fill-current' : 'opacity-30'}`} />)}
            </span>
        );
    }
    if (field?.type === 'yesno') return <span className="capitalize">{String(value)}</span>;
    return <span>{String(value)}</span>;
}

export default function AdminFeedbackSubmissionsPage() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [exporting, setExporting] = useState(false);

    const exportCsv = async () => {
        setExporting(true);
        try {
            const res = await api.get('/admin/feedback/export', { responseType: 'blob' });
            const cd = res.headers['content-disposition'] || '';
            const match = cd.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `feedback_export_${Date.now()}.csv`;
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Feedback exported to CSV');
        } catch (e) {
            toast.error('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const [subs, f] = await Promise.all([
                    api.get('/admin/feedback-submissions'),
                    api.get('/admin/feedback-form'),
                ]);
                setItems(subs.data.submissions || []);
                setForm(f.data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const fieldById = (id) => form?.fields?.find((f) => f.id === id);

    return (
        <AdminLayout
            title="Feedback submissions"
            subtitle="Post-session feedback from mentors"
            actions={
                <Button
                    onClick={exportCsv}
                    disabled={exporting}
                    data-testid="feedback-export-csv-button"
                >
                    {exporting ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting…</>
                    ) : (
                        <><Download className="mr-2 h-4 w-4" /> Export CSV</>
                    )}
                </Button>
            }
        >
            {loading ? (
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : isSubHidden('feedback_submissions', 'mentor_feedback') ? (
                <Card>
                    <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="feedback-mentor-hidden">
                        <MessageSquare className="mx-auto mb-3 h-6 w-6" />
                        Mentor feedback is not available for your account.
                    </CardContent>
                </Card>
            ) : items.length === 0 ? (
                <Card>
                    <CardContent className="p-10 text-center text-sm text-muted-foreground">
                        <MessageSquare className="mx-auto mb-3 h-6 w-6" />
                        No feedback submitted yet. Once mentors fill their post-session feedback, it will show up here.
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {items.map((s) => (
                        <Card key={s.id} className="card-lift cursor-pointer" onClick={() => setSelected(s)}>
                            <CardContent className="p-5">
                                <div className="flex items-center justify-between">
                                    <div className="font-display text-base font-semibold">{s.mentor_name}</div>
                                    <Badge variant="secondary">{formatDateTime(s.submitted_at)}</Badge>
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                    Student: {s.student_name} · Session: {formatDateTime(s.session_start)}
                                </div>
                                <div className="mt-3 line-clamp-3 text-sm text-muted-foreground">
                                    {Object.entries(s.answers).slice(0, 2).map(([id, val]) => (
                                        <div key={id}><strong>{fieldById(id)?.label || id}:</strong> {String(val).slice(0, 80)}</div>
                                    ))}
                                </div>
                                <div className="mt-3 text-xs text-primary">View full submission →</div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display">Feedback for {selected?.student_name}</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4 text-sm">
                            <div className="rounded-lg border border-border/60 bg-accent/30 p-3">
                                <div><strong>Mentor:</strong> {selected.mentor_name}</div>
                                <div><strong>Student:</strong> {selected.student_name} ({selected.student_email})</div>
                                <div><strong>Session:</strong> {formatDateTime(selected.session_start)}</div>
                                <div><strong>Submitted:</strong> {formatDateTime(selected.submitted_at)}</div>
                            </div>
                            <div className="space-y-3">
                                {form?.fields?.map((f) => (
                                    <div key={f.id} className="rounded-lg border border-border/60 bg-card p-3">
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground">{f.label}</div>
                                        <div className="mt-1">{renderAnswer(f, selected.answers[f.id])}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
